import React, { useState, useEffect, useContext, createContext, useCallback } from 'react';
import './App.css';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser({ token });
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/login`, { username, password });
      const { access_token } = response.data;
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser({ token: access_token });
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Custom hook for debouncing
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Login Component
const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const success = await login(username, password);
    if (!success) {
      setError('Nieprawidłowe dane logowania');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Panel Administracyjny
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Scraper Cen Produktów
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Nazwa użytkownika"
              />
            </div>
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Hasło"
              />
            </div>
          </div>

          {error && (
            <div className="text-red-600 text-sm text-center">{error}</div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? 'Logowanie...' : 'Zaloguj się'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Dashboard Component
const Dashboard = () => {
  const [stats, setStats] = useState({});
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [config, setConfig] = useState({});
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showAddSite, setShowAddSite] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  
  // Config form
  const [configForm, setConfigForm] = useState({
    interval_hours: 3,
    enabled: true
  });

  // Scraping status
  const [scrapingStatus, setScrapingStatus] = useState({
    status: 'idle',
    message: '',
    progress: 0,
    current_site: null,
    products_scraped: 0
  });
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSite, setSelectedSite] = useState('');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalProducts, setTotalProducts] = useState(0);
  const [siteCounts, setSiteCounts] = useState({});
  
  // Product selection and deletion
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  
  const { logout } = useContext(AuthContext);

  const [newSite, setNewSite] = useState({
    name: '',
    base_url: '',
    product_urls: [''],
    selectors: {
      product_card: '',
      name: '',
      price: '',
      image: 'img'
    },
    pagination: {
      type: 'auto', // 'auto', 'query_param', 'path_param', 'brute_force'
      param_name: 'page',
      format: '?page={page}' // Template for URL generation
    },
    enabled: true
  });

  useEffect(() => {
    fetchData();
    fetchScrapingStatus();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [currentPage, pageSize, searchTerm, selectedSite]);

  useEffect(() => {
    // Reset to page 1 when filters change
    setCurrentPage(1);
  }, [searchTerm, selectedSite]);

  // Polling for scraping status
  useEffect(() => {
    let interval;
    
    if (scrapingStatus.status === 'running') {
      interval = setInterval(() => {
        fetchScrapingStatus();
      }, 5000); // Check every 5 seconds when running
    } else {
      // Check less frequently when idle
      interval = setInterval(() => {
        fetchScrapingStatus();
      }, 30000); // Check every 30 seconds when idle
    }
    
    return () => clearInterval(interval);
  }, [scrapingStatus.status]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('page_size', pageSize);
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      if (selectedSite) {
        params.append('site', selectedSite);
      }
      
      console.log('Fetching products with params:', params.toString());
      
      // Fetch products and stats
      const [productsRes, statsRes] = await Promise.all([
        axios.get(`${API}/products?${params}`),
        axios.get(`${API}/products/stats?${params}`)
      ]);
      
      console.log('Products data received:', {
        products: productsRes.data.length,
        total: statsRes.data.total_count,
        sites: Object.keys(statsRes.data.site_counts || {})
      });
      
      setProducts(productsRes.data);
      setTotalProducts(statsRes.data.total_count);
      setSiteCounts(statsRes.data.site_counts);
      
    } catch (error) {
      console.error('Error fetching products:', error);
      console.error('Error details:', error.response?.data);
      
      // Clear potentially invalid token on auth errors
      if (error.response?.status === 401) {
        console.log('Authentication error - clearing token');
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        window.location.reload();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchScrapingStatus = async () => {
    try {
      const response = await axios.get(`${API}/scraping/status`);
      setScrapingStatus(response.data);
    } catch (error) {
      console.error('Error fetching scraping status:', error);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedSite('');
    setPriceRange({ min: '', max: '' });
    setSortBy('name');
    setSortOrder('asc');
    setCurrentPage(1);
    setSelectedProducts(new Set());
    setSelectAllChecked(false);
  };

  // Product selection functions
  const handleProductSelect = (productId) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
    setSelectAllChecked(newSelected.size === products.length && products.length > 0);
  };

  const handleSelectAll = () => {
    if (selectAllChecked) {
      setSelectedProducts(new Set());
      setSelectAllChecked(false);
    } else {
      const allIds = new Set(products.map(p => p.id));
      setSelectedProducts(allIds);
      setSelectAllChecked(true);
    }
  };

  const deleteSelectedProducts = async () => {
    if (selectedProducts.size === 0) {
      alert('Nie wybrano żadnych produktów do usunięcia.');
      return;
    }

    if (!window.confirm(`Czy na pewno chcesz usunąć ${selectedProducts.size} wybranych produktów?`)) {
      return;
    }

    try {
      setLoading(true);
      const productIds = Array.from(selectedProducts);
      await axios.delete(`${API}/products/bulk`, {
        data: productIds,
        headers: { 'Content-Type': 'application/json' }
      });
      
      alert(`Usunięto ${selectedProducts.size} produktów`);
      setSelectedProducts(new Set());
      setSelectAllChecked(false);
      fetchProducts();
      fetchData();
    } catch (error) {
      console.error('Error deleting products:', error);
      alert('Błąd podczas usuwania produktów');
    } finally {
      setLoading(false);
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten produkt?')) {
      return;
    }

    try {
      setLoading(true);
      await axios.delete(`${API}/products/${productId}`);
      alert('Produkt usunięty');
      setSelectedProducts(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
      fetchProducts();
      fetchData();
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Błąd podczas usuwania produktu');
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      console.log('Fetching dashboard data...');
      const [statsRes, sessionsRes, configRes, sitesRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/scraping/sessions`),
        axios.get(`${API}/scraping/config`),
        axios.get(`${API}/scraping/sites`)
      ]);

      console.log('Dashboard data received:', {
        stats: statsRes.data,
        sessions: sessionsRes.data.length,
        sites: sitesRes.data.length
      });

      setStats(statsRes.data);
      setSessions(sessionsRes.data);
      setConfig(configRes.data);
      setSites(sitesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      console.error('Error details:', error.response?.data);
      
      // Clear potentially invalid token on auth errors
      if (error.response?.status === 401) {
        console.log('Authentication error - clearing token');
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        window.location.reload();
      }
    } finally {
      setLoading(false);
    }
  };

  const startScraping = async () => {
    try {
      setLoading(true);
      await axios.post(`${API}/scraping/start`);
      alert('Scrapowanie rozpoczęte!');
      
      // Refresh data and start polling status
      setTimeout(() => {
        fetchData();
        fetchProducts();
        fetchScrapingStatus();
      }, 2000);
    } catch (error) {
      console.error('Error starting scraping:', error);
      alert('Błąd podczas rozpoczęcia scrapowania. Sprawdź połączenie z serwerem.');
    } finally {
      setLoading(false);
    }
  };

  const updateConfig = async (newConfig) => {
    try {
      await axios.put(`${API}/scraping/config`, newConfig);
      setConfig(newConfig);
      alert('Konfiguracja zaktualizowana!');
    } catch (error) {
      console.error('Error updating config:', error);
      alert('Błąd podczas aktualizacji konfiguracji');
    }
  };

  const addSite = async () => {
    try {
      await axios.post(`${API}/scraping/sites`, newSite);
      setShowAddSite(false);
      setNewSite({
        name: '',
        base_url: '',
        product_urls: [''],
        selectors: {
          product_card: '',
          name: '',
          price: '',
          image: 'img'
        },
        pagination: {
          type: 'auto',
          param_name: 'page',
          format: '?page={page}'
        },
        enabled: true
      });
      fetchData();
      alert('Strona dodana pomyślnie!');
    } catch (error) {
      console.error('Error adding site:', error);
      alert('Błąd podczas dodawania strony');
    }
  };

  const deleteSite = async (siteId) => {
    if (window.confirm('Czy na pewno chcesz usunąć tę stronę?')) {
      try {
        await axios.delete(`${API}/scraping/sites/${siteId}`);
        fetchData();
        alert('Strona usunięta pomyślnie!');
      } catch (error) {
        console.error('Error deleting site:', error);
        alert('Błąd podczas usuwania strony');
      }
    }
  };

  const updateSite = async (siteId, siteData) => {
    try {
      await axios.put(`${API}/scraping/sites/${siteId}`, siteData);
      setEditingSite(null);
      fetchData();
      alert('Strona zaktualizowana pomyślnie!');
    } catch (error) {
      console.error('Error updating site:', error);
      alert('Błąd podczas aktualizacji strony');
    }
  };

  const addUrl = (index) => {
    const newUrls = [...newSite.product_urls];
    newUrls.splice(index + 1, 0, '');
    setNewSite({...newSite, product_urls: newUrls});
  };

  const removeUrl = (index) => {
    const newUrls = newSite.product_urls.filter((_, i) => i !== index);
    setNewSite({...newSite, product_urls: newUrls});
  };

  const updateUrl = (index, value) => {
    const newUrls = [...newSite.product_urls];
    newUrls[index] = value;
    setNewSite({...newSite, product_urls: newUrls});
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN'
    }).format(price);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '---';
    return new Date(dateString).toLocaleString('pl-PL');
  };

  // Get unique sites for filter dropdown (combine configured sites and existing products)
  const availableSites = [...new Set([
    ...sites.map(site => site.base_url ? site.base_url.replace(/^https?:\/\//, '').replace(/^www\./, '') : site.name.toLowerCase()),
    ...Object.keys(siteCounts || {})
  ])].sort();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Ładowanie...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <h1 className="text-2xl font-bold text-gray-900">
              Monitor Cen - Panel Admina
            </h1>
            <button
              onClick={logout}
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
            >
              Wyloguj
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {[
              { id: 'dashboard', label: 'Dashboard' },
              { id: 'products', label: 'Produkty' },
              { id: 'price-changes', label: 'Zmiany cen' },
              { id: 'sessions', label: 'Sesje' },
              { id: 'sites', label: 'Strony' },
              { id: 'settings', label: 'Ustawienia' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold">P</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Produkty
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {stats.total_products || 0}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold">S</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Strony
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {stats.sites_monitored || 0}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold">C</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Zmiany cen (24h)
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {stats.price_changes_24h || 0}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold">L</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Ostatnie skanowanie
                        </dt>
                        <dd className="text-sm font-medium text-gray-900">
                          {stats.last_scrape ? formatDate(stats.last_scrape) : 'Brak danych'}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Szybkie akcje
                </h3>
                <div className="mt-5">
                  <button
                    onClick={startScraping}
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {loading ? 'Scrapowanie...' : 'Aktualizuj ceny'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'products' && (
          <div className="space-y-6">
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Produkty ({totalProducts})
                  </h3>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setActiveTab('price-changes')}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Zmiany cen
                    </button>
                    <button
                      onClick={startScraping}
                      disabled={loading}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {loading ? 'Scrapowanie...' : 'Odśwież'}
                    </button>
                  </div>
                </div>

                {/* Scraping Progress Indicator */}
                {scrapingStatus.status === 'running' && (
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                        <span className="text-sm font-medium text-blue-800">
                          {scrapingStatus.message}
                        </span>
                      </div>
                      <span className="text-sm text-blue-600">
                        {scrapingStatus.products_scraped} produktów
                      </span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${scrapingStatus.progress}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-xs text-blue-600 mt-1">
                      <span>Postęp: {scrapingStatus.progress}%</span>
                      {scrapingStatus.current_site && (
                        <span>Aktualnie: {scrapingStatus.current_site}</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Search and Filter Controls */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Search */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Szukaj po nazwie
                      </label>
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Wpisz nazwę produktu..."
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    {/* Site Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Filtruj po sklepie
                      </label>
                      <select
                        value={selectedSite}
                        onChange={(e) => setSelectedSite(e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Wszystkie sklepy</option>
                        {availableSites.map(site => (
                          <option key={site} value={site}>
                            {site} ({siteCounts[site] || 0})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Page Size */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Produktów na stronę
                      </label>
                      <select
                        value={pageSize}
                        onChange={(e) => setPageSize(parseInt(e.target.value))}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={-1}>Wszystkie</option>
                      </select>
                    </div>

                    {/* Clear Filters */}
                    <div className="flex items-end">
                      <button
                        onClick={clearFilters}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Wyczyść filtry
                      </button>
                    </div>

                    {/* Bulk Delete */}
                    {selectedProducts.size > 0 && (
                      <div className="flex items-end">
                        <button
                          onClick={deleteSelectedProducts}
                          disabled={loading}
                          className="w-full px-4 py-2 bg-red-600 text-white rounded-md shadow-sm text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                        >
                          Usuń wybrane ({selectedProducts.size})
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <input
                            type="checkbox"
                            checked={selectAllChecked}
                            onChange={handleSelectAll}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Produkt
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Strona
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Cena aktualna
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Cena poprzednia
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Najniższa w historii
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Akcje
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Zmiana
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Aktualizacja
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {products.map((product) => (
                        <tr key={product.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedProducts.has(product.id)}
                              onChange={() => handleProductSelect(product.id)}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {product.image_url && (
                                <img
                                  className="h-10 w-10 rounded-full mr-3"
                                  src={product.image_url}
                                  alt={product.name}
                                />
                              )}
                              <div className="text-sm font-medium text-gray-900">
                                {product.product_url ? (
                                  <a 
                                    href={product.product_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="hover:text-indigo-600 hover:underline"
                                  >
                                    {product.name.length > 60 ? product.name.substring(0, 60) + '...' : product.name}
                                  </a>
                                ) : (
                                  <span>{product.name.length > 60 ? product.name.substring(0, 60) + '...' : product.name}</span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                              {product.site}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col">
                              <div className="text-sm font-medium text-gray-900">
                                {formatPrice(product.price)}
                              </div>
                              {product.promotional_price && product.original_price && (
                                <div className="text-xs text-gray-500 line-through">
                                  {formatPrice(product.original_price)}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {product.previous_price ? formatPrice(product.previous_price) : '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col">
                              {product.historical_lowest_price ? (
                                <>
                                  <div className={`text-sm font-medium ${
                                    product.price <= product.historical_lowest_price ? 'text-green-600' : 'text-gray-900'
                                  }`}>
                                    {formatPrice(product.historical_lowest_price)}
                                  </div>
                                  {product.historical_lowest_date && (
                                    <div className="text-xs text-gray-500">
                                      {formatDate(product.historical_lowest_date)}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="text-sm text-gray-400">-</div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {product.price_difference && (
                              <div className="flex flex-col">
                                <div className={`text-sm font-medium ${
                                  product.price_difference > 0 ? 'text-red-600' : 'text-green-600'
                                }`}>
                                  {product.price_difference > 0 ? '+' : ''}{formatPrice(product.price_difference)}
                                </div>
                                <div className={`text-xs ${
                                  product.price_difference > 0 ? 'text-red-500' : 'text-green-500'
                                }`}>
                                  {product.price_change_percentage > 0 ? '+' : ''}{product.price_change_percentage}%
                                </div>
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => deleteProduct(product.id)}
                              disabled={loading}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50"
                              title="Usuń produkt"
                            >
                              🗑️
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(product.last_scraped)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {pageSize !== -1 && (
                  <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                      >
                        Poprzednia
                      </button>
                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage * pageSize >= totalProducts}
                        className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                      >
                        Następna
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          Strona <span className="font-medium">{currentPage}</span> z{' '}
                          <span className="font-medium">{Math.ceil(totalProducts / pageSize)}</span>
                          {' '}(łącznie {totalProducts} produktów)
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                          <button
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                          >
                            Poprzednia
                          </button>
                          
                          {/* Page Numbers */}
                          {Array.from({ length: Math.min(5, Math.ceil(totalProducts / pageSize)) }).map((_, i) => {
                            const page = i + 1;
                            return (
                              <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                  currentPage === page
                                    ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                }`}
                              >
                                {page}
                              </button>
                            );
                          })}
                          
                          <button
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage * pageSize >= totalProducts}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                          >
                            Następna
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'sessions' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Sesje scrapowania
              </h3>
              <div className="space-y-4">
                {sessions.map((session) => (
                  <div key={session.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium text-gray-900">
                          Sesja {session.id.substring(0, 8)}
                        </h4>
                        <p className="text-sm text-gray-500">
                          Rozpoczęto: {formatDate(session.started_at)}
                        </p>
                        {session.completed_at && (
                          <p className="text-sm text-gray-500">
                            Zakończono: {formatDate(session.completed_at)}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          session.status === 'completed' ? 'bg-green-100 text-green-800' :
                          session.status === 'failed' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {session.status}
                        </span>
                        <p className="text-sm text-gray-500 mt-1">
                          {session.products_scraped} produktów
                        </p>
                      </div>
                    </div>
                    {session.errors && session.errors.length > 0 && (
                      <div className="mt-3 p-3 bg-red-50 rounded-md">
                        <h5 className="text-sm font-medium text-red-800">Błędy:</h5>
                        <ul className="text-sm text-red-700 mt-1">
                          {session.errors.map((error, index) => (
                            <li key={index}>• {error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'price-changes' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Produkty ze zmianami cen
                </h3>
                <button
                  onClick={() => setActiveTab('products')}
                  className="text-sm text-indigo-600 hover:text-indigo-900"
                >
                  Wszystkie produkty →
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Produkt
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Strona
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cena aktualna
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cena poprzednia
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Zmiana
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Aktualizacja
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {products.filter(p => p.has_price_change).map((product) => (
                      <tr key={product.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {product.image_url && (
                              <img
                                className="h-10 w-10 rounded-full mr-3"
                                src={product.image_url}
                                alt={product.name}
                              />
                            )}
                            <div className="text-sm font-medium text-gray-900">
                              {product.product_url ? (
                                <a 
                                  href={product.product_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="hover:text-indigo-600 hover:underline"
                                >
                                  {product.name.length > 60 ? product.name.substring(0, 60) + '...' : product.name}
                                </a>
                              ) : (
                                <span>{product.name.length > 60 ? product.name.substring(0, 60) + '...' : product.name}</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                            {product.site}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col">
                            <div className="text-sm font-medium text-gray-900">
                              {formatPrice(product.price)}
                            </div>
                            {product.promotional_price && product.original_price && (
                              <div className="text-xs text-gray-500 line-through">
                                {formatPrice(product.original_price)}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {product.previous_price ? formatPrice(product.previous_price) : '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {product.price_difference && (
                            <div className="flex flex-col">
                              <div className={`text-sm font-medium ${
                                product.price_difference > 0 ? 'text-red-600' : 'text-green-600'
                              }`}>
                                {product.price_difference > 0 ? '+' : ''}{formatPrice(product.price_difference)}
                              </div>
                              <div className={`text-xs ${
                                product.price_difference > 0 ? 'text-red-500' : 'text-green-500'
                              }`}>
                                {product.price_change_percentage > 0 ? '+' : ''}{product.price_change_percentage}%
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(product.last_scraped)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {products.filter(p => p.has_price_change).length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500">Brak produktów ze zmianami cen.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'sites' && (
          <div className="space-y-6">
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Zarządzanie stronami do scrapowania
                  </h3>
                  <button
                    onClick={() => setShowAddSite(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    + Dodaj stronę
                  </button>
                </div>
                
                <div className="space-y-4">
                  {sites.map((site) => (
                    <div key={site.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{site.name}</h4>
                          <p className="text-sm text-gray-600">{site.base_url}</p>
                          <div className="mt-2">
                            <p className="text-sm text-gray-500">URLe produktów:</p>
                            <ul className="text-sm text-gray-400 ml-4">
                              {site.product_urls.map((url, index) => (
                                <li key={index}>• {url}</li>
                              ))}
                            </ul>
                          </div>
                          <div className="mt-2">
                            <p className="text-sm text-gray-500">Selektory:</p>
                            <div className="text-sm text-gray-400 ml-4">
                              <p>Karta produktu: <code>{site.selectors.product_card}</code></p>
                              <p>Nazwa: <code>{site.selectors.name}</code></p>
                              <p>Cena: <code>{site.selectors.price}</code></p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={site.enabled}
                              onChange={(e) => updateSite(site.id, {...site, enabled: e.target.checked})}
                              className="mr-2"
                            />
                            <span className="text-sm text-gray-700">Aktywne</span>
                          </label>
                          <button
                            onClick={() => setEditingSite(site)}
                            className="text-indigo-600 hover:text-indigo-900 text-sm"
                          >
                            Edytuj
                          </button>
                          <button
                            onClick={() => deleteSite(site.id)}
                            className="text-red-600 hover:text-red-900 text-sm"
                          >
                            Usuń
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal dodawania strony */}
        {showAddSite && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-screen overflow-y-auto">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Dodaj nową stronę</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nazwa strony</label>
                  <input
                    type="text"
                    value={newSite.name}
                    onChange={(e) => setNewSite({...newSite, name: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="np. sklep.pl"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Adres bazowy</label>
                  <input
                    type="url"
                    value={newSite.base_url}
                    onChange={(e) => setNewSite({...newSite, base_url: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="https://sklep.pl"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">URLe kategorii produktów</label>
                  {newSite.product_urls.map((url, index) => (
                    <div key={index} className="flex items-center mt-2">
                      <input
                        type="url"
                        value={url}
                        onChange={(e) => updateUrl(index, e.target.value)}
                        className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                        placeholder="https://sklep.pl/kategoria"
                      />
                      <button
                        onClick={() => addUrl(index)}
                        className="ml-2 px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                      >
                        +
                      </button>
                      {newSite.product_urls.length > 1 && (
                        <button
                          onClick={() => removeUrl(index)}
                          className="ml-2 px-3 py-2 bg-red-200 text-red-700 rounded-md hover:bg-red-300"
                        >
                          -
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Selektory CSS</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600">Karta produktu</label>
                      <input
                        type="text"
                        value={newSite.selectors.product_card}
                        onChange={(e) => setNewSite({...newSite, selectors: {...newSite.selectors, product_card: e.target.value}})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        placeholder=".product-card"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600">Nazwa produktu</label>
                      <input
                        type="text"
                        value={newSite.selectors.name}
                        onChange={(e) => setNewSite({...newSite, selectors: {...newSite.selectors, name: e.target.value}})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        placeholder="h2, .product-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600">Cena</label>
                      <input
                        type="text"
                        value={newSite.selectors.price}
                        onChange={(e) => setNewSite({...newSite, selectors: {...newSite.selectors, price: e.target.value}})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        placeholder=".price, .product-price"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600">Obrazek</label>
                      <input
                        type="text"
                        value={newSite.selectors.image}
                        onChange={(e) => setNewSite({...newSite, selectors: {...newSite.selectors, image: e.target.value}})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        placeholder="img"
                      />
                    </div>
                  </div>
                  
                  {/* Sekcja konfiguracji paginacji */}
                  <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-md">
                    <h4 className="font-medium text-gray-900 mb-3">⚙️ Konfiguracja paginacji (opcjonalne)</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Typ paginacji</label>
                        <select
                          value={newSite.pagination.type}
                          onChange={(e) => setNewSite({...newSite, pagination: {...newSite.pagination, type: e.target.value}})}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        >
                          <option value="auto">Auto-detekcja (zalecane)</option>
                          <option value="query_param">Parametr w URL (?page=N)</option>
                          <option value="path_param">Parametr w ścieżce (/page/N)</option>
                          <option value="brute_force">Brute force (1, 2, 3...)</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Nazwa parametru</label>
                        <input
                          type="text"
                          value={newSite.pagination.param_name}
                          onChange={(e) => setNewSite({...newSite, pagination: {...newSite.pagination, param_name: e.target.value}})}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                          placeholder="page, counter, strona"
                          disabled={newSite.pagination.type === 'auto' || newSite.pagination.type === 'brute_force'}
                        />
                      </div>
                      
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700">Format URL</label>
                        <input
                          type="text"
                          value={newSite.pagination.format}
                          onChange={(e) => setNewSite({...newSite, pagination: {...newSite.pagination, format: e.target.value}})}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                          placeholder="?page={page} lub /page/{page} lub ?counter={page}"
                          disabled={newSite.pagination.type === 'auto' || newSite.pagination.type === 'brute_force'}
                        />
                      </div>
                    </div>
                    
                    <div className="mt-3 text-sm text-gray-600">
                      {newSite.pagination.type === 'auto' && "✅ System automatycznie wykryje format paginacji"}
                      {newSite.pagination.type === 'query_param' && `🔗 Przykład: ${newSite.base_url}${newSite.pagination.format.replace('{page}', '2')}`}
                      {newSite.pagination.type === 'path_param' && `🔗 Przykład: ${newSite.base_url}${newSite.pagination.format.replace('{page}', '2')}`}
                      {newSite.pagination.type === 'brute_force' && "🔄 System będzie testować strony po kolei (1, 2, 3...)"}
                    </div>
                  </div>
                  
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
                    <h4 className="font-medium text-blue-900 mb-2">💡 Wskazówki dotyczące paginacji:</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li><strong>RMDbike:</strong> Paginacja: /kategoria/96/2, /kategoria/96/3, etc.</li>
                      <li><strong>Miejskiesporty:</strong> Paginacja: ?counter=2, ?counter=3, etc.</li>
                      <li><strong>SkatePro:</strong> Paginacja: ?page=2, ?page=3, etc.</li>
                      <li><strong>Ogólne:</strong> System automatycznie wykrywa format paginacji</li>
                    </ul>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newSite.enabled}
                    onChange={(e) => setNewSite({...newSite, enabled: e.target.checked})}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Strona aktywna</span>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowAddSite(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Anuluj
                </button>
                <button
                  onClick={addSite}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Dodaj stronę
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal edycji strony */}
        {editingSite && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-screen overflow-y-auto">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Edytuj stronę</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nazwa strony</label>
                  <input
                    type="text"
                    value={editingSite.name}
                    onChange={(e) => setEditingSite({...editingSite, name: e.target.value})}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="np. Scootive"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Bazowy URL</label>
                  <input
                    type="url"
                    value={editingSite.base_url}
                    onChange={(e) => setEditingSite({...editingSite, base_url: e.target.value})}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="https://example.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">URLe produktów</label>
                  <div className="space-y-2">
                    {editingSite.product_urls.map((url, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="url"
                          value={url}
                          onChange={(e) => {
                            const newUrls = [...editingSite.product_urls];
                            newUrls[index] = e.target.value;
                            setEditingSite({...editingSite, product_urls: newUrls});
                          }}
                          className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="https://example.com/kategoria"
                        />
                        {editingSite.product_urls.length > 1 && (
                          <button
                            onClick={() => {
                              const newUrls = editingSite.product_urls.filter((_, i) => i !== index);
                              setEditingSite({...editingSite, product_urls: newUrls});
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            Usuń
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      onClick={() => setEditingSite({...editingSite, product_urls: [...editingSite.product_urls, '']})}
                      className="text-indigo-600 hover:text-indigo-900 text-sm"
                    >
                      + Dodaj URL
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Selektor karty produktu</label>
                    <input
                      type="text"
                      value={editingSite.selectors.product_card}
                      onChange={(e) => setEditingSite({...editingSite, selectors: {...editingSite.selectors, product_card: e.target.value}})}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder=".product-card"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Selektor nazwy</label>
                    <input
                      type="text"
                      value={editingSite.selectors.name}
                      onChange={(e) => setEditingSite({...editingSite, selectors: {...editingSite.selectors, name: e.target.value}})}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder=".product-name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Selektor ceny</label>
                    <input
                      type="text"
                      value={editingSite.selectors.price}
                      onChange={(e) => setEditingSite({...editingSite, selectors: {...editingSite.selectors, price: e.target.value}})}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder=".product-price"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Selektor obrazu</label>
                    <input
                      type="text"
                      value={editingSite.selectors.image}
                      onChange={(e) => setEditingSite({...editingSite, selectors: {...editingSite.selectors, image: e.target.value}})}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="img"
                    />
                  </div>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editingSite.enabled}
                    onChange={(e) => setEditingSite({...editingSite, enabled: e.target.checked})}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Strona aktywna</span>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setEditingSite(null)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Anuluj
                </button>
                <button
                  onClick={() => updateSite(editingSite.id, editingSite)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Zapisz zmiany
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6">
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Ustawienia scrapowania
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Minimalny interwał (godziny)
                      </label>
                      <input
                        type="number"
                        value={config.interval_hours_min || 2}
                        onChange={(e) => setConfig({...config, interval_hours_min: parseInt(e.target.value)})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        min="1"
                        max="12"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Maksymalny interwał (godziny)
                      </label>
                      <input
                        type="number"
                        value={config.interval_hours_max || 3}
                        onChange={(e) => setConfig({...config, interval_hours_max: parseInt(e.target.value)})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        min="1"
                        max="24"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={config.enabled || false}
                        onChange={(e) => setConfig({...config, enabled: e.target.checked})}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Automatyczne scrapowanie włączone
                      </span>
                    </label>
                    <p className="text-sm text-gray-500 mt-1">
                      Scrapowanie będzie wykonywane losowo w przedziale {config.interval_hours_min || 2}-{config.interval_hours_max || 3} godzin
                    </p>
                  </div>
                  <div>
                    <button
                      onClick={() => updateConfig(config)}
                      className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
                    >
                      Zapisz ustawienia
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

// Main App Component
const App = () => {
  const { user, loading } = useContext(AuthContext);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return user ? <Dashboard /> : <Login />;
};

// App with Context Provider
const AppWithContext = () => {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
};

export default AppWithContext;